import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment-timezone";
import { of } from "rxjs";
import { AuthenticationService } from "src/app/core/authentication/authentication.service";
import { LocalStorageKeys } from "src/app/core/storage/local-storage";
import { NotificationService } from "src/app/shared/components/notification/notification.service";
import { UserTokens } from "src/app/shared/types/app.type";
import { GenerateMfaCodeResponse } from "./multifactor-auth.types";
import { LoadingService } from "src/app/core/loader/loading.service";
import { LoginLayoutData } from "../index-layout/index-layout.type";

@Component({
  selector: "app-multifactor-auth",
  templateUrl: "./multifactor-auth.component.html",
  styleUrls: ["./multifactor-auth.component.scss"],
})
export class MultifactorAuthComponent implements OnInit {
  readonly defaultPath = "/dashboard";
  readonly secondsInAMinute = 60;
  authForm: FormGroup;
  mfaToken: string;
  codeDate: string;
  countdown: string;
  resendEnabled: boolean;
  layoutData: LoginLayoutData = {
    image: "assets/GlobeLogo.svg",
    boxStyles: {
      margin: "2rem 0 2rem",
    },
  };

  constructor(
    private formBuilder: FormBuilder,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoadingService,
  ) {}

  get f() {
    return this.authForm.controls;
  }

  get showTimer() {
    return !!this.codeDate;
  }

  ngOnInit(): void {
    this.mfaToken = JSON.parse(localStorage.getItem(LocalStorageKeys.mfa))?.mfaToken;
    this.codeDate = JSON.parse(localStorage.getItem(LocalStorageKeys.mfa))?.createdAt;

    if (!this.mfaToken) {
      this.router.navigate(["/login"]);
    }

    this.setCodeTimer();

    this.authForm = this.formBuilder.group({
      mfaCode: ["", [Validators.required, Validators.maxLength(6), Validators.pattern("^[0-9]*$")]],
    });
  }

  onSubmit() {
    this.loader.show();
    if (this.authForm.invalid || !this.mfaToken) return;
    this.authForm.disable();
    this.authenticationService.verifyMfaCode(this.mfaToken, this.f.mfaCode.value).subscribe(
      (tokens: UserTokens) => {
        this.authenticationService.setSession(of(tokens), false).subscribe(() => {
          localStorage.removeItem(LocalStorageKeys.mfa);
          const redirectUrl = this.route.snapshot.queryParams["redirectUrl"] || this.defaultPath;
          location.href = redirectUrl;
        });
      },
      (error) => {
        this.authForm.enable();
        console.error(error);
        const err = error || "Error validating verification code";
        this.notificationService.error(err);
        this.loader.hide();
      },
    );
  }

  generateMfaCode() {
    if (this.mfaToken) {
      this.authenticationService.generateMfaCode(this.mfaToken).subscribe(
        (response: GenerateMfaCodeResponse) => {
          this.codeDate = response.createdAt;
          this.setCodeTimer();
          const mfaData = {
            mfaToken: this.mfaToken,
            createdAt: response.createdAt,
          };
          localStorage.removeItem(LocalStorageKeys.mfa);
          localStorage.setItem(LocalStorageKeys.mfa, JSON.stringify(mfaData));
          this.notificationService.success("Verification code successfully sent");
        },
        (error) => {
          console.error(error);
          const err = "Error generating verification code. Please wait a minute and try again";
          this.notificationService.error(err);
        },
      );
    }
  }

  timer(seconds: number) {
    const timer = setInterval(() => {
      seconds--;
      const prefix = seconds < 10 ? "0" : "";

      if (seconds == 0) {
        clearInterval(timer);
        this.resendEnabled = true;
      }
      this.countdown = `00:${prefix}${seconds}`;
    }, 1000);
  }

  setCodeTimer() {
    if (this.codeDate) {
      this.resendEnabled = false;
      let codeDate = moment(this.codeDate);
      const now = moment();
      // this check is required to avoid async issues
      if (now.isBefore(codeDate)) {
        codeDate = now;
      }
      const timeDifference = moment(now.diff(codeDate));
      if (timeDifference.minutes() < 1) {
        this.timer(this.secondsInAMinute - timeDifference.seconds());
      } else {
        this.resendEnabled = true;
      }
    }
  }
}
